import React, { useState } from 'react';
import { toast } from 'sonner';
import { Copy, Check, RefreshCw, ExternalLink, QrCode, Info } from 'lucide-react';
import { Panel, Empty, StatusBadge, Disclaimer, Badge } from '../../components/Primitives';
import { useFetch } from '../../lib/hooks';
import { DepositWallets } from '../../components/DepositWallets';
import { api, errMsg, fmtUsd, fmtDate, shortHash } from '../../lib/api';

export default function Deposits() {
  const { data: config } = useFetch('/config');
  const { data: deposits, reload } = useFetch('/deposits', [], 30000);
  const [tx, setTx] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmNet, setConfirmNet] = useState(false);

  const addr = config?.deposit_address || '';
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&bgcolor=101512&color=39FF14&data=${encodeURIComponent(addr)}`;

  const copy = () => {
    navigator.clipboard.writeText(addr);
    setCopied(true);
    toast.success('Deposit address copied');
    setTimeout(() => setCopied(false), 1800);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!confirmNet) return toast.error('Please confirm you sent on BNB Smart Chain (BEP20)');
    if (!/^0x[a-fA-F0-9]{64}$/.test(tx.trim())) return toast.error('Enter a valid 0x… transaction hash');
    setSubmitting(true);
    try {
      const { data } = await api.post('/deposits/submit', { tx_hash: tx.trim() });
      toast.success(`Deposit detected — ${data.status.replace(/_/g, ' ')}`);
      setTx('');
      reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSubmitting(false); }
  };

  const refresh = async (id) => {
    try { await api.post(`/deposits/${id}/refresh`); reload(); toast.success('Refreshed from chain'); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6" data-testid="deposits-page">
      <div className="grid gap-6 lg:grid-cols-5">
        <Panel title="USDT (BEP20) Deposit Address" className="lg:col-span-3" testId="deposit-address-panel">
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="shrink-0 grid place-items-center">
              <div className="p-2 rounded-xl bg-vj-charcoal border border-vj-neon/30">
                <img src={qr} alt="Deposit QR" width={160} height={160} className="rounded-lg" data-testid="deposit-qr" />
              </div>
              <span className="text-[11px] text-vj-grey mt-2 flex items-center gap-1"><QrCode size={12} /> Scan to deposit</span>
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <label className="text-xs text-vj-grey">Address</label>
                <div className="mt-1 flex items-center gap-2">
                  <input readOnly value={addr} className="flex-1 h-10 px-3 rounded-lg bg-vj-charcoal border border-border font-mono text-xs text-foreground" data-testid="deposit-address-input" />
                  <button onClick={copy} data-testid="copy-address-btn" className="h-10 w-10 grid place-items-center rounded-lg border border-border hover:border-vj-neon/50 transition-colors">
                    {copied ? <Check size={16} className="text-vj-neon" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-vj-grey">Network</div><div>{config?.network}</div></div>
                <div><div className="text-xs text-vj-grey">Chain ID</div><div className="font-mono">{config?.chain_id}</div></div>
                <div><div className="text-xs text-vj-grey">Min deposit</div><div className="font-mono">{config?.min_deposit} USDT</div></div>
                <div><div className="text-xs text-vj-grey">Confirmations</div><div className="font-mono">{config?.confirmations_required}</div></div>
              </div>
              <div className="text-[11px] text-vj-grey break-all">
                USDT contract: <span className="font-mono">{config?.contract}</span>
              </div>
            </div>
          </div>
          <div className="mt-4 rounded-lg border border-vj-gold/30 bg-vj-gold/5 px-3 py-2.5 text-xs text-vj-gold/90 flex gap-2">
            <Info size={14} className="shrink-0 mt-0.5" />
            This is a shared deposit address. Link your sending wallet below and deposits are detected and attributed automatically. Sent from an unlinked wallet? Submit the transaction hash and an admin will attribute it. Only send verified USDT on BEP20. Never send other networks or assets.
          </div>
        </Panel>

        <Panel title="Submit Transaction" className="lg:col-span-2" testId="deposit-submit-panel">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs text-vj-grey">Transaction hash</label>
              <input value={tx} onChange={(e) => setTx(e.target.value)} placeholder="0x…" className="mt-1 w-full h-11 px-3 rounded-lg bg-vj-charcoal border border-border font-mono text-sm text-foreground focus:border-vj-neon/60 focus:outline-none" data-testid="deposit-tx-input" />
            </div>
            <label className="flex items-start gap-2 text-xs text-vj-grey cursor-pointer">
              <input type="checkbox" checked={confirmNet} onChange={(e) => setConfirmNet(e.target.checked)} className="mt-0.5 accent-vj-neon" data-testid="deposit-network-confirm" />
              <span>I confirm I sent verified USDT on the BNB Smart Chain (BEP20).</span>
            </label>
            <button disabled={submitting} className="w-full h-11 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm hover:-translate-y-0.5 transition-all vj-glow-green disabled:opacity-60" data-testid="deposit-submit-btn">
              {submitting ? 'Verifying on-chain…' : 'Verify Deposit'}
            </button>
          </form>
          <p className="text-[11px] text-vj-grey mt-3">We verify the transaction directly on BNB Smart Chain. Deposits are credited after reaching the required confirmations and admin review.</p>
        </Panel>
      </div>

      <DepositWallets onChange={reload} />

      <Panel title="Deposit History" testId="deposit-history" action={<Badge tone="blue">On-chain verified · BNB Smart Chain</Badge>}>
        {deposits && deposits.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-vj-grey border-b border-border">
                <th className="py-2 font-medium">Amount</th><th className="font-medium">Tx Hash</th><th className="font-medium">Confirmations</th><th className="font-medium">Status</th><th className="font-medium">Date</th><th></th>
              </tr></thead>
              <tbody>
                {deposits.map((d) => (
                  <tr key={d.id} className="border-b border-border last:border-0">
                    <td className="py-3 font-mono">{fmtUsd(d.amount)}</td>
                    <td><a href={`https://bscscan.com/tx/${d.tx_hash}`} target="_blank" rel="noreferrer" className="font-mono text-xs text-vj-neon inline-flex items-center gap-1">{shortHash(d.tx_hash)}<ExternalLink size={11} /></a></td>
                    <td className="font-mono text-xs">{d.confirmations >= d.required_confirmations ? `${d.required_confirmations}+` : d.confirmations}/{d.required_confirmations}</td>
                    <td><div className="flex flex-wrap gap-1"><StatusBadge status={d.status} />{!d.user_id && <StatusBadge status="awaiting_attribution" />}</div></td>
                    <td className="text-xs text-vj-grey">{fmtDate(d.created_at)}</td>
                    <td><button onClick={() => refresh(d.id)} className="text-vj-grey hover:text-vj-neon" data-testid={`deposit-refresh-${d.id}`}><RefreshCw size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>No deposits yet. Send USDT and submit the transaction hash above.</Empty>}
      </Panel>

      <Disclaimer>Deposit crediting and trading allocation follow the platform's configured automation policy. Ambiguous deposits are flagged for review rather than auto-assigned.</Disclaimer>
    </div>
  );
}
